import * as msal from "@azure/msal-browser";
import { v4 as uuidv4 } from "uuid";

// Msal Configurations
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_MICROSOFT_AUTH_CLIENT_ID,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: process.env.REACT_APP_MICROSOFT_AUTH_REDIRECT
  },
  cache: {
    cacheLocation: "localStorage"
  }
};
export const msalInstance = new msal.PublicClientApplication(msalConfig);

let interaction = null;
const tokenRequests = new Map();

// All interactive requests use the same queue, including login and consent
// popups requested by token acquisition. Do not clear MSAL's storage locks:
// they can belong to a live Microsoft window.
function runInteraction(action) {
  const request = interaction ? interaction.catch(() => {}).then(action) : action();
  interaction = request;
  const release = () => { if (interaction === request) interaction = null; };
  request.then(release, release);
  return request;
}

export function loginMicrosoftPopup() {
  return runInteraction(() => msalInstance.loginPopup(loginRequest));
}

// Authentication Request Parameters
export const loginRequest = {
  scopes: process.env.REACT_APP_MICROSOFT_AUTH_SCOPE_LOGIN.split(",")
};
export const apiTokenRequest = {
  scopes: [
    process.env.REACT_APP_MICROSOFT_AUTH_SCOPE_ACCESS_API
  ]
};
export const outlookTokenRequest = {
  scopes: [
    process.env.REACT_APP_MICROSOFT_AUTH_SCOPE_ACCESS_OUTLOOK
  ]
};

const adminConsent = {
  clientId: process.env.REACT_APP_MICROSOFT_AUTH_OUTLOOK_CLIENT_ID,
  redirectUrl: process.env.REACT_APP_MICROSOFT_AUTH_ADMIN_CONSENT_REDIRECT
}

/**
 * Get the access token from Microsoft provider
 */
export function getAccessTokenMicrosoft(email, tokenRequest, interactive = false) {
  const key = JSON.stringify([email, tokenRequest, interactive]);
  if (tokenRequests.has(key)) return tokenRequests.get(key);
  const request = acquireMicrosoftToken(email, tokenRequest, interactive);
  tokenRequests.set(key, request);
  const release = () => { if (tokenRequests.get(key) === request) tokenRequests.delete(key); };
  request.then(release, release);
  return request;
}

async function acquireMicrosoftToken(email, tokenRequest, interactive = false) {
  const account = msalInstance.getAccountByUsername(email);
  const request = { ...tokenRequest, account, loginHint: email };
  // E-mail/password console sessions do not necessarily have a Microsoft account cached.
  if (!account) interactive = true;
  let accessToken = null;

  if (!interactive) { // request token silently, by default
    console.debug('Acquire Token Microsoft silently');
    try {
      const result = await msalInstance.acquireTokenSilent(request);
      accessToken = result.accessToken;
    } catch (error) {
      if (error instanceof msal.InteractionRequiredAuthError) {
        console.warn("Unable to adquire token silently, trying it interactively.");
        interactive = true; // try the interactive approach
      } else {
        console.error("Unable to adquire token silently.");
        throw error;
      }
    }
  }

  if (interactive) { // request token interactively, if needed
    console.debug('Acquire Token Microsoft interactively');
    try {
      const waiting = !!interaction;
      const result = await runInteraction(async () => {
        // Another popup may have just obtained this account/consent. Try its
        // cached result before opening another window for the waiting request.
        if (waiting) {
          const cached = msalInstance.getAccountByUsername(email);
          if (cached) {
            try { return await msalInstance.acquireTokenSilent({ ...request, account: cached }); }
            catch (error) { if (!(error instanceof msal.InteractionRequiredAuthError)) throw error; }
          }
        }
        return msalInstance.acquireTokenPopup(request);
      });
      accessToken = result.accessToken;
    } catch (error) {
      console.error("Unable to adquire token interactively.");
      throw error;
    }
  }

  // console.debug('Acquire Token Microsoft result', accessToken);
  return accessToken;
}

export async function acquireAdminConsent(user) {
  console.debug('Acquire Admin Consent');

  const state = uuidv4();
  const account = msalInstance.getAccountByUsername(user.email);
  const tenantId = account ? account.tenantId : user.tenant;
  // A console e-mail/password session can request consent for its verified domain.
  const tenant = tenantId || user.domain;
  if (!tenant) throw new Error('Microsoft organization is not available');
  const url = `https://login.microsoftonline.com/${encodeURIComponent(tenant)}/adminconsent`;
  const queryString = '?' + new URLSearchParams({
    client_id: adminConsent.clientId, state, redirect_uri: adminConsent.redirectUrl
  });
  const name = "adminConsent"

  const popupWindow = openSizedPopup(url + queryString, name);
  if (!popupWindow) {
    console.error("Error opening popup window.");
    throw msal.BrowserAuthError.createEmptyWindowCreatedError();
  }
  if (popupWindow.focus) {
    popupWindow.focus();
  }
  
  try {
    const ok = await monitorAdminConsent(popupWindow, tenantId, state);

    popupWindow.close();

    return ok;
  } catch (error) {
    console.error("Error adquiring admin consent.", error);
    popupWindow.close();
    return Promise.reject(error);
  }
}

const POPUP_WIDTH = 483;
const POPUP_HEIGHT = 600;

function openSizedPopup(urlNavigate, popupName) {
  const winLeft = window.screenLeft ? window.screenLeft : window.screenX;
  const winTop = window.screenTop ? window.screenTop : window.screenY;

  const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
  const left = Math.max(0, ((width / 2)) - (POPUP_WIDTH / 2) + winLeft);
  const top = Math.max(0, ((height / 2)) - (POPUP_HEIGHT / 2) + winTop);

  return window.open(urlNavigate, popupName, `width=${POPUP_WIDTH}, height=${POPUP_HEIGHT}, top=${top}, left=${left}, scrollbars=yes`);
}

const TIMEOUT = 120000;
const POLLING_INTERVAL_MS = 50;

function monitorAdminConsent(contentWindow, tenant, state) {
  return new Promise((resolve, reject) => {

    const deadline = Date.now() + TIMEOUT;
    const redirect = new URL(adminConsent.redirectUrl);

    console.debug("monitorAdminConsent polling started");

    const intervalId = setInterval(() => {
      if (contentWindow.closed) {
        console.error("monitorAdminConsent window closed");
        clearInterval(intervalId);
        reject(msal.BrowserAuthError.createUserCancelledError());
        return;
      }

      if (Date.now() >= deadline) {
        clearInterval(intervalId);
        reject(msal.BrowserAuthError.createMonitorPopupTimeoutError());
        return;
      }

      let location;
      try {
        location = new URL(contentWindow.location.href);
      } catch (e) { return; } // Microsoft pages are cross-origin until the callback.
      if (location.origin !== redirect.origin || location.pathname !== redirect.pathname) return;
      const params = location.searchParams;
      if (params.get('state') !== state) {
        clearInterval(intervalId);
        reject(new msal.AuthError('state_mismatch', 'Invalid Microsoft consent response'));
      } else if (params.has("error")) {
        console.error("monitorAdminConsent error returned");
        clearInterval(intervalId);
        reject(new msal.AuthError(params.get("error"), params.get("error_description")));
      } else if (paramsAreValid(params, tenant, state)) {
        console.debug("monitorAdminConsent consent adquired");
        clearInterval(intervalId);
        resolve({ "admin_consent": true });
      } else {
        clearInterval(intervalId);
        reject(new msal.AuthError('invalid_consent_response', 'Invalid Microsoft consent response'));
      }
    }, POLLING_INTERVAL_MS);
  });
}

function paramsAreValid(params, tenant, state) {
  const returnedTenant = params.get('tenant');
  const validTenant = tenant ? tenant === returnedTenant : /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(returnedTenant || '');
  return "True" === params.get("admin_consent") && validTenant
    && state === params.get("state");
}
